import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { createRegistration } from "../lib/api";
import { useRecaptcha } from "../hooks/useRecaptcha";
import { Users, UserCheck, Calendar, Mail, Phone, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import RecaptchaWrapper from "../components/RecaptchaWrapper";

const TEAM_OPTIONS = [
  { value: "U13", label: "U13" },
  { value: "U15", label: "U15" },
  { value: "U17", label: "U17" },
  { value: "C", label: "C" },
  { value: "JONG", label: "Jong FTG" },
  { value: "EERSTE", label: "Eerste ploeg" },
];

const PLAYER_TYPES = [
  { value: "competitief", label: "Competitief", description: "Deelname aan officiële competities" },
  { value: "recreatief", label: "Recreatief", description: "Voor plezier en conditie" },
];

function InschrijvingenPage() {
  const { isDark } = useOutletContext();
  const { verifyRecaptcha } = useRecaptcha();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    player_type: "",
    team_preference: "",
    first_name: "",
    last_name: "",
    date_of_birth: "",
    email: "",
    additional_email: "",
    phone: "",
    candidacy_acknowledged: false,
    regulations_acknowledged: false,
    remarks: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.candidacy_acknowledged) {
      toast.error("Je moet bevestigen dat dit een kandidatuur is");
      return;
    }

    if (!formData.regulations_acknowledged) {
      toast.error("Je moet bevestigen dat je het intern reglement hebt gelezen");
      return;
    }

    setIsSubmitting(true);

    try {
      let recaptchaToken = null;
      try {
        recaptchaToken = await verifyRecaptcha("registration");
      } catch (e) {
        console.warn("reCAPTCHA verification skipped:", e);
      }

      await createRegistration({
        ...formData,
        recaptcha_token: recaptchaToken,
      });

      setIsSubmitted(true);
      toast.success("Inschrijving succesvol ontvangen! We nemen zo snel mogelijk contact met je op.");
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className={`min-h-screen py-24 px-4 ${isDark ? 'bg-[#0a1628]' : 'bg-slate-50'}`}>
        <div className="container mx-auto max-w-2xl">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white'}>
            <CardContent className="py-16 text-center">
              <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                isDark ? 'bg-green-500/20' : 'bg-green-100'
              }`}>
                <CheckCircle2 className={`w-10 h-10 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Bedankt voor je inschrijving!
              </h2>
              <p className={`mb-8 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                We hebben je kandidatuur ontvangen en nemen zo snel mogelijk contact met je op om de volgende stappen te bespreken.
              </p>
              <Button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    player_type: "",
                    team_preference: "",
                    first_name: "",
                    last_name: "",
                    date_of_birth: "",
                    email: "",
                    additional_email: "",
                    phone: "",
                    candidacy_acknowledged: false,
                    regulations_acknowledged: false,
                    remarks: "",
                  });
                }}
                variant="outline"
                className={isDark ? 'border-slate-600 hover:bg-slate-700' : ''}
              >
                Nieuwe inschrijving
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-24 px-4 ${isDark ? 'bg-[#0a1628]' : 'bg-slate-50'}`}>
      <div className="container mx-auto max-w-3xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Inschrijvingen
          </h1>
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Word lid van Futsal Gent en speel mee in onze teams
          </p>
        </div>

        {/* Info Banner */}
        <Card className={`mb-8 ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              <div>
                <p className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  Belangrijk
                </p>
                <p className={`text-sm ${isDark ? 'text-amber-300/80' : 'text-amber-600'}`}>
                  Dit formulier dient als kandidatuur voor een plaats in onze teams. Na ontvangst van je aanvraag 
                  nemen we contact met je op voor een kennismakingstraining. Definitieve inschrijving gebeurt 
                  pas na goedkeuring door de technische staf.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Registration Form */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Users className="w-5 h-5" />
              Inschrijvingsformulier
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Vul alle verplichte velden in om je kandidatuur in te dienen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="registration-form">
              {/* Player Type Selection */}
              <div className="space-y-4">
                <Label className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Type speler *
                </Label>
                <div className="grid md:grid-cols-2 gap-4">
                  {PLAYER_TYPES.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => handleSelectChange("player_type", type.value)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.player_type === type.value
                          ? isDark 
                            ? 'border-amber-500 bg-amber-500/10' 
                            : 'border-amber-500 bg-amber-50'
                          : isDark
                            ? 'border-slate-600 hover:border-slate-500 bg-slate-700/50'
                            : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                      }`}
                      data-testid={`player-type-${type.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          formData.player_type === type.value
                            ? 'border-amber-500 bg-amber-500'
                            : isDark ? 'border-slate-500' : 'border-slate-300'
                        }`}>
                          {formData.player_type === type.value && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {type.label}
                          </p>
                          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {type.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team Preference */}
              <div className="space-y-2">
                <Label htmlFor="team_preference" className={isDark ? 'text-slate-200' : ''}>
                  <UserCheck className="w-4 h-4 inline mr-2" />
                  Voorkeur team/afdeling *
                </Label>
                <Select
                  value={formData.team_preference}
                  onValueChange={(value) => handleSelectChange("team_preference", value)}
                  required
                >
                  <SelectTrigger 
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                    data-testid="team-preference-select"
                  >
                    <SelectValue placeholder="Selecteer een team" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
                    {TEAM_OPTIONS.map((team) => (
                      <SelectItem 
                        key={team.value} 
                        value={team.value}
                        className={isDark ? 'text-white hover:bg-slate-700' : ''}
                      >
                        {team.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Personal Info */}
              <div className="space-y-4">
                <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Persoonlijke gegevens
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className={isDark ? 'text-slate-200' : ''}>
                      Voornaam *
                    </Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className={isDark ? 'text-slate-200' : ''}>
                      Achternaam *
                    </Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date_of_birth" className={isDark ? 'text-slate-200' : ''}>
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Geboortedatum *
                  </Label>
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    required
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                    data-testid="date-of-birth-input"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Contactgegevens
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className={isDark ? 'text-slate-200' : ''}>
                      <Mail className="w-4 h-4 inline mr-2" />
                      E-mailadres *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="additional_email" className={isDark ? 'text-slate-200' : ''}>
                      <Mail className="w-4 h-4 inline mr-2" />
                      Extra e-mailadres
                    </Label>
                    <Input
                      id="additional_email"
                      name="additional_email"
                      type="email"
                      value={formData.additional_email}
                      onChange={handleInputChange}
                      placeholder="Bijv. ouder/voogd"
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : ''}
                      data-testid="additional-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className={isDark ? 'text-slate-200' : ''}>
                    <Phone className="w-4 h-4 inline mr-2" />
                    Telefoonnummer *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+32 XXX XX XX XX"
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : ''}
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label htmlFor="remarks" className={isDark ? 'text-slate-200' : ''}>
                  <FileText className="w-4 h-4 inline mr-2" />
                  Extra opmerkingen
                </Label>
                <Textarea
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Bijv. eerdere futsal ervaring, beschikbaarheid, medische informatie..."
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : ''}
                  data-testid="remarks-textarea"
                />
              </div>

              {/* Acknowledgment */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="candidacy_acknowledged"
                    checked={formData.candidacy_acknowledged}
                    onCheckedChange={(checked) => handleSelectChange("candidacy_acknowledged", checked)}
                    className={isDark ? 'border-slate-500' : ''}
                    data-testid="candidacy-checkbox"
                  />
                  <div className="space-y-1">
                    <Label 
                      htmlFor="candidacy_acknowledged" 
                      className={`font-medium cursor-pointer ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      Ik begrijp dat dit een kandidatuur is *
                    </Label>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Door dit formulier in te dienen, ga ik akkoord dat dit een kandidatuur betreft voor 
                      een plaats in de geselecteerde afdeling. Definitieve inschrijving gebeurt pas na 
                      goedkeuring door de technische staf van Futsal Gent.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="regulations_acknowledged"
                    checked={formData.regulations_acknowledged}
                    onCheckedChange={(checked) => handleSelectChange("regulations_acknowledged", checked)}
                    className={isDark ? 'border-slate-500' : ''}
                    data-testid="regulations-checkbox"
                  />
                  <div className="space-y-1">
                    <Label
                      htmlFor="regulations_acknowledged"
                      className={`font-medium cursor-pointer ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      Ik heb het intern reglement gelezen *
                    </Label>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Lees het reglement via{" "}
                      <a
                        href="/Futsal_Gent_Intern_ReglementS25_26.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-amber-500"
                      >
                        dit document
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || !formData.player_type || !formData.team_preference}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold py-6"
                data-testid="submit-registration-btn"
              >
                {isSubmitting ? "Versturen..." : "Inschrijving versturen"}
              </Button>

              {/* Privacy Note */}
              <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Door dit formulier in te dienen ga je akkoord met ons{" "}
                <a href="/privacy" className="underline hover:text-amber-500">
                  privacybeleid
                </a>
                . Je gegevens worden uitsluitend gebruikt voor het verwerken van je kandidatuur.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function Inschrijvingen() {
  return (
    <RecaptchaWrapper>
      <InschrijvingenPage />
    </RecaptchaWrapper>
  );
}
